import { camelCaseToUserText } from 'src/utils/stringUtils';

export enum DisplayType {
  DATE = 'date',
  DATETIME = 'datetime',
  CURRENCY = 'currency',
  JSON = 'json',
}
abstract class TetherTableColumn<T> {
  /** The name/key of the attribute to display in this column */
  attribute: keyof T;

  /** The text to display for the column header, will use the attribute name if not set */
  label?: string;

  /** Sets the DisplayType option that is used for automatically formatting and sorting certain types of values, e.g. dates */
  displayType?: DisplayType;

  /** Override the render for the data value */
  renderOverride?: (attributeValue: any, displayObject: T) => (string | JSX.Element);

  /** Gets the value of the attribute; can be overridden to allow for external values in sorting */
  getValue? = (displayObject: T) => displayObject[this.attribute];

  /** Can the column be sorted */
  sortable?: boolean;

  /**
   * The API's name for the quantity this column shows, for a SERVER-paged table.
   *
   * `attribute` is a render key, not a sort key, and several columns legitimately share
   * one: the device list declares `{ attribute: 'id', label: 'Assigned To Customer' }` and
   * `{ attribute: 'id', label: 'Type' }` because both render from the row's id via
   * getValue. Sorting by `attribute` therefore made three different headers all re-query
   * sorted by device UUID — the table visibly reordered, so the click looked like it had
   * worked. Set this to the field the API accepts; leave it unset and the header is not
   * clickable in server mode.
   */
  sortField?: string;

  /** Override the default sort function (alphabetical */
  sort?: (a: T, b: T) => number;

  /** The name/key of the attribute to display as subtext */
  subTextAttribute?: keyof T;

  /** Gets the value of the attribute; can be overridden to allow for external values in sorting */
  getSubtextValue? = (displayObject: T) => (this.subTextAttribute ? displayObject[this.subTextAttribute] : null);

  /** If set this column will not be visible by default (note it can only be turned on if column selection is enabled on the table) */
  hideByDefault?: boolean;

  /** Will group adjacent columns with the same name together if groups are enabled on the table */
  groupName?: string;

  /** Disables word wrap for values displayed in this column */
  disableWrap?: boolean;

  /** Enables a button that will copy the value of this column to clipboard */
  copyToClipboard?: boolean;

  /** Column will be excluded from all table operations - used for conditionally visible column */
  disabled?: boolean;
}

export default TetherTableColumn;

export const getColumnLabel = (column: TetherTableColumn<any>) => (
  column.label || camelCaseToUserText(column.attribute as string)
);
